import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { FlexModule } from '@angular/flex-layout/flex';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import {
  GuarantorKybSelectionsEvent,
  GuarantorValue,
  KybInitiateCorporatePartyFn,
  KybRefreshFn,
  KybResultFn,
} from '@portal-workspace/grow-shared-library';
import { setupUntilDestroy } from '../component-utils';
import { requiredAllowEmptyValidator } from '../../validators/validators';
import { BusinessNumberSearchFn } from '../business-number-search-component/business-number-search.component';
import { GuarantorComponent } from '../individual-and-entity-info-component/guarantor.component';

export interface AddGuarantorDialogData {
  applicationId: number;
  businessNumberSearchFn: BusinessNumberSearchFn;
  kybResultFn: KybResultFn;
  kybRefreshFn: KybRefreshFn;
  /** Persists a guarantor entity and starts its company check, so its key people can be ticked here. */
  kybInitiateCorporatePartyFn: KybInitiateCorporatePartyFn;
}

export interface AddGuarantorDialogResult {
  cancelled: boolean;
  /** Every guarantor row the dialog holds, existing entities included; only the individuals are new. */
  guarantors: GuarantorValue;
  kybSelections: GuarantorKybSelectionsEvent | null;
}

/**
 * Add Guarantor on the KYC/AML tab, hosting the submission wizard's Additional Guarantors step so
 * both kinds of guarantor are added the same way there and here: an entity by ABN, whose company
 * check runs while the dialog waits so its directors and shareholders can be ticked, or a named
 * individual.
 *
 * Opened directly with MatDialog rather than through ApplicationDialogService: the step's component
 * injects that service, and importing it into the service would make the two files a cycle.
 */
@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
  selector: 'add-guarantor-dialog',
  templateUrl: './add-guarantor.dialog.html',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    FlexModule,
    GuarantorComponent,
  ],
})
export class AddGuarantorDialog implements OnInit {
  subscriptions: Subscription[] = [];

  formControlGuarantors: FormControl<GuarantorValue>;
  kybSelections: GuarantorKybSelectionsEvent | null = null;

  @ViewChild(GuarantorComponent) private guarantorComponent?: GuarantorComponent;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AddGuarantorDialogData,
    private matDialogRef: MatDialogRef<AddGuarantorDialog, AddGuarantorDialogResult>,
    formBuilder: FormBuilder,
  ) {
    this.formControlGuarantors = formBuilder.control(null, [requiredAllowEmptyValidator]);
  }

  ngOnInit(): void {
    setupUntilDestroy(this);
  }

  onCancel() {
    this.matDialogRef.close({ cancelled: true, guarantors: null, kybSelections: null });
  }

  onDone() {
    // The step propagates null while any row is incomplete, so marking first is what puts the
    // reason on screen instead of leaving Done looking broken.
    this.guarantorComponent?.mark();
    if (this.formControlGuarantors.invalid) {
      return;
    }
    this.matDialogRef.close({
      cancelled: false,
      guarantors: this.formControlGuarantors.value,
      kybSelections: this.kybSelections,
    });
  }
}
